import React, { useCallback, MouseEvent, memo } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { Box, makeStyles, createStyles } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import {
  IIntelligentRecommendationsEventTypeEnum,
  IShipmentRecommendationsSourceEnum,
} from 'types/FMSGraphqlTypes'
import { useFeatureFlagContext } from 'utils/context/FeatureFlagsContext'
import { useMarketplaceContext } from 'utils/context/MarketplaceContext'
import { useTrackConversionContext } from 'apps/fleet-planner/context/track-conversion-source'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { Button, Card } from 'transfix-ui'
import { useTrackIntelligentRecs } from '../../recommendations/RecommendedShipmentsSection/hooks'
import {
  ITrackingRecSource,
  getTrackingRecSource,
} from '../../recommendations/RecommendedShipmentsSection/utils'
import ShipmentCardDetails, { IShipmentCardDetailsProps } from '../ShipmentCardDetails'

export interface IShipmentCardProps extends Omit<IShipmentCardDetailsProps, 'isNewLayoutEnabled'> {
  /**
   * @default IShipmentRecommendationsSourceEnum.lane_alert_auto_historical
   */
  recommendationSource?: IShipmentRecommendationsSourceEnum
  dataTest?: string
  indexInList?: number
}

const useShipmentCardStyles = makeStyles(theme => {
  return createStyles<
    'card' | 'bookButton' | 'hiddenButtons',
    { isUnviewed?: boolean; variant?: string }
  >({
    hiddenButtons: {
      display: 'none',
    },
    card: {
      boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.15)',
      borderRadius: '4px',
      minWidth: '195px',
      padding: theme.spacing(2),
      border: props =>
        props.isUnviewed && props.variant === 'historical'
          ? '1px solid #2298FE'
          : '1px solid #CED4DA',
      '&:hover': {
        backgroundColor: brandColors.coolGray1,
        cursor: 'pointer',
      },
      '&:hover $hiddenButtons': {
        whiteSpace: 'nowrap',
        padding: `${theme.spacing(1.5)} ${theme.spacing(2)}`,
        display: 'flex',
        position: 'absolute',
        bottom: '0',
        right: '0',
        width: '100%',
        borderTop: `1px solid ${brandColors.coolGray4}`,
        background: brandColors.white,
      },
    },
    bookButton: {
      marginRight: theme.spacing(1),
    },
  })
})

const ShipmentCard = memo<IShipmentCardProps>(
  ({
    dataTest,
    recommendation,
    recommendationSource = IShipmentRecommendationsSourceEnum.lane_alert_auto_historical,
    variant = 'historical',
    indexInList,
  }) => {
    const { shipmentId, unviewed: isUnviewed } = recommendation || {}
    const { trackEvent } = useFMSAnalytics()
    const { trackIntelligentRecEvent } = useTrackIntelligentRecs()
    const history = useHistory()
    const location = useLocation()
    const classes = useShipmentCardStyles({ isUnviewed: isUnviewed || true, variant })
    const { setActiveShipmentDetailsId } = useMarketplaceContext()
    const { setTrackConversion } = useTrackConversionContext()
    const { getFlag } = useFeatureFlagContext()
    const { enabled: enableFmsGraphqlRecs } = getFlag('enableFmsGraphqlRecs')

    const recType = getTrackingRecSource(recommendationSource)

    const callSnowflakeClickEvent = useCallback(() => {
      if (indexInList && enableFmsGraphqlRecs && recType === ITrackingRecSource.INTELLIGENT) {
        trackIntelligentRecEvent({
          shipmentIds: [Number(shipmentId)],
          shipmentOrder: [indexInList],
          intelligentRecsEventType: IIntelligentRecommendationsEventTypeEnum.click,
        })
      }
    }, [enableFmsGraphqlRecs, indexInList, recType, shipmentId, trackIntelligentRecEvent])

    const openShipmentDetailsHandler = useCallback(() => {
      const route = variant === 'historical' ? 'historical-lanes' : 'book'
      callSnowflakeClickEvent()
      if (enableFmsGraphqlRecs) {
        if (location.pathname.includes('/home')) {
          trackEvent('Homepage', 'HOME_RECS_CLICKED_REC_CARD')
        } else {
          trackEvent('Loads for You', `CLICKED_${recType}_REC_CARD`)
        }
      } else if (variant === 'historical') {
        trackEvent('Historical Lanes', 'VIEWED_LOAD_ON_RUN_IT_AGAIN_PAGE')
      } else {
        trackEvent('Book Load', 'VIEWED_RUN_IT_AGAIN_LOAD')
      }
      setTrackConversion('web', recommendationSource)
      history.replace(`/${route}/${shipmentId}`)
      setActiveShipmentDetailsId(Number(shipmentId))
    }, [
      variant,
      callSnowflakeClickEvent,
      enableFmsGraphqlRecs,
      setTrackConversion,
      recommendationSource,
      history,
      shipmentId,
      setActiveShipmentDetailsId,
      trackEvent,
      recType,
      location.pathname,
    ])

    const openRateConfirmationPageHandler = useCallback(
      (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation()
        callSnowflakeClickEvent()
        if (enableFmsGraphqlRecs) {
          if (location.pathname.includes('/home')) {
            trackEvent('Homepage', 'HOME_RECS_CLICKED_BOOK_LOAD_BUTTON')
          } else {
            trackEvent('Loads for You', `CLICKED_${recType}_REC_BOOK_LOAD_BUTTON`)
          }
        } else {
          trackEvent('Historical Lanes', 'CLICKED_HOVER_BOOK_BUTTON')
        }
        setTrackConversion('web', recommendationSource)
        history.push(`/book/${shipmentId}/rate-confirmation`)
      },
      [
        callSnowflakeClickEvent,
        enableFmsGraphqlRecs,
        history,
        location.pathname,
        recType,
        recommendationSource,
        setTrackConversion,
        shipmentId,
        trackEvent,
      ]
    )

    const openBidModalHandler = useCallback(
      (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation()
        callSnowflakeClickEvent()
        if (enableFmsGraphqlRecs) {
          if (location.pathname.includes('/home')) {
            trackEvent('Homepage', 'HOME_RECS_CLICKED_SUBMIT_BID_BUTTON')
          } else {
            trackEvent('Loads for You', `CLICKED_${recType}_REC_SUBMIT_BID_BUTTON`)
          }
        } else {
          trackEvent('Historical Lanes', 'CLICKED_HOVER_BID_BUTTON')
        }
        setTrackConversion('web', recommendationSource)
        history.push(`/book/${shipmentId}/bid`)
      },
      [
        callSnowflakeClickEvent,
        enableFmsGraphqlRecs,
        history,
        location.pathname,
        recType,
        recommendationSource,
        setTrackConversion,
        shipmentId,
        trackEvent,
      ]
    )

    return (
      <Box data-test={dataTest} onClick={openShipmentDetailsHandler}>
        <Card className={classes.card}>
          <ShipmentCardDetails
            variant={variant}
            recommendation={recommendation}
            isNewLayoutEnabled={enableFmsGraphqlRecs}
          />
          <Box className={classes.hiddenButtons}>
            <Button
              className={classes.bookButton}
              fullWidth
              onClick={openRateConfirmationPageHandler}
              color='primary'>
              Book Load
            </Button>
            <Button fullWidth onClick={openBidModalHandler} color='secondary'>
              Submit Bid
            </Button>
          </Box>
        </Card>
      </Box>
    )
  }
)

export default ShipmentCard
